/**
 * Per-series chart studies — SMA stack + Wilder RSI + MACD + a bias reading.
 *
 * Computed per request over the filtered close set (architecture §0). The
 * database stores facts; this module owns the derivation so Chart Terminal
 * and Technical Analysis cannot drift apart.
 */
import { MA_PERIODS, RSI_PERIOD, type MaPeriod } from '../constants/moving-average';

import { macdSeries, type MacdSeries } from './macd';
import { lastDefined, lastSimpleMovingAverage, simpleMovingAverage } from './moving-average';
import { relativeStrengthIndex } from './relative-strength';

/** RSI at or above this is stretched to the upside — do not chase. */
export const RSI_OVERBOUGHT = 70;
/** RSI at or below this is stretched to the downside — not a buy by itself. */
export const RSI_OVERSOLD = 30;
/** Slowest-first lookbacks for the regime MA. Derived from `MA_PERIODS`. */
export const REGIME_MA_PERIODS: readonly MaPeriod[] = MA_PERIODS.reduce<readonly MaPeriod[]>(
  (acc, period) => [period, ...acc],
  [],
);

export type StudyBias = 'bullish' | 'bearish' | 'neutral';

export interface ChartStudySma {
  readonly 50: number | null;
  readonly 100: number | null;
  readonly 150: number | null;
  readonly 200: number | null;
}

export interface ChartStudyMacd {
  readonly line: number | null;
  readonly signal: number | null;
  readonly histogram: number | null;
}

export const EMPTY_MACD: ChartStudyMacd = {
  line: null,
  signal: null,
  histogram: null,
};

export interface ChartStudySnapshot {
  readonly last: number | null;
  readonly sma: ChartStudySma;
  readonly rsi: number | null;
  readonly macd: ChartStudyMacd;
}

/** Lookback snapshot plus the aligned oscillator series the panes tail. */
export interface ChartStudies {
  readonly snapshot: ChartStudySnapshot;
  readonly rsi: readonly (number | null)[];
  readonly macd: MacdSeries;
}

export interface MaOverlay {
  readonly period: MaPeriod;
  readonly values: readonly (number | null)[];
}

/**
 * Trailing bar only — a non-finite last close is missing, not the prior close.
 * SMA/RSI still walk back via `lastSimpleMovingAverage` / `lastDefined`; those
 * are last *formed* values, not a substitute last close.
 */
function lastClose(bars: readonly number[]): number | null {
  const value = bars[bars.length - 1];
  if (value === undefined || !Number.isFinite(value)) return null;
  return value;
}

function smaLast(bars: readonly number[], period: MaPeriod): number | null {
  return lastSimpleMovingAverage(bars, period);
}

function macdLast(series: MacdSeries): ChartStudyMacd {
  return {
    line: lastDefined(series.macd),
    signal: lastDefined(series.signal),
    histogram: lastDefined(series.histogram),
  };
}

export function chartStudies(bars: readonly number[]): ChartStudies {
  const rsi = relativeStrengthIndex(bars, RSI_PERIOD);
  const macd = macdSeries(bars);
  return {
    snapshot: {
      last: lastClose(bars),
      sma: {
        50: smaLast(bars, 50),
        100: smaLast(bars, 100),
        150: smaLast(bars, 150),
        200: smaLast(bars, 200),
      },
      rsi: lastDefined(rsi),
      macd: macdLast(macd),
    },
    rsi,
    macd,
  };
}

export function chartStudySnapshot(bars: readonly number[]): ChartStudySnapshot {
  return chartStudies(bars).snapshot;
}

/** Last `count` slots of a study series so a pane can share the price viewport. */
export function alignedStudyTail<T>(values: readonly T[], count: number): readonly T[] {
  if (count <= 0) return [];
  if (values.length <= count) return values;
  return values.slice(values.length - count);
}

export function movingAverageOverlays(bars: readonly number[]): readonly MaOverlay[] {
  return MA_PERIODS.map((period) => ({
    period,
    values: simpleMovingAverage(bars, period),
  }));
}

export function regimePeriod(sma: ChartStudySma): MaPeriod | null {
  const period = REGIME_MA_PERIODS.find((key) => sma[key] !== null);
  return period ?? null;
}

function stretchOf(rsi: number | null): 'overbought' | 'oversold' | 'mid' {
  if (rsi === null) return 'mid';
  if (rsi >= RSI_OVERBOUGHT) return 'overbought';
  if (rsi <= RSI_OVERSOLD) return 'oversold';
  return 'mid';
}

function biasInRegime(up: boolean, stretch: 'overbought' | 'oversold' | 'mid'): StudyBias {
  if (up) return stretch === 'overbought' ? 'neutral' : 'bullish';
  return stretch === 'oversold' ? 'neutral' : 'bearish';
}

/**
 * Directional reading: slowest formed MA is the regime; RSI 70/30 is stretch.
 *
 * Stretch never inverts the regime. An overbought uptrend demotes to neutral
 * (do not chase). An oversold downtrend demotes to neutral (not a buy).
 * Missing RSI leaves the regime sign intact. No formed MA → null, never a
 * direction inferred from RSI alone (R2b).
 */
export function studyBias(snapshot: ChartStudySnapshot): StudyBias | null {
  if (snapshot.last === null) return null;
  const period = regimePeriod(snapshot.sma);
  if (period === null) return null;
  const ma = snapshot.sma[period];
  if (ma === null || snapshot.last === ma) return 'neutral';
  return biasInRegime(snapshot.last > ma, stretchOf(snapshot.rsi));
}
